import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CreatorsLandingPage = () => {
    const [carouselIndex, setCarouselIndex] = useState(0);

    const testimonials = [
        {
            text: "“Oye Creators gave me the clarity I needed to improve my content strategy. I saw consistent growth within weeks.”",
            handle: "creator_handle",
            followers: "95K followers",
            avatarColor: "bg-indigo-100 text-indigo-600"
        },
        {
            text: "“Understanding my audience became simpler. Now I know what to post and what to stop posting.”",
            handle: "creator_handle",
            followers: "134K followers",
            avatarColor: "bg-pink-100 text-pink-600"
        },
        {
            text: "“I saved hours on competitor research. The tracking lists changed how I plan content.”",
            handle: "creator_handle",
            followers: "1.1M followers",
            avatarColor: "bg-purple-100 text-purple-600"
        }
    ];

    const nextSlide = () => {
        setCarouselIndex((prev) => Math.min(testimonials.length - 1, prev + 1));
    };

    const prevSlide = () => {
        setCarouselIndex((prev) => Math.max(0, prev - 1));
    };

    return (
        <div className="min-h-screen font-sans bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 text-gray-900 overflow-hidden">
            <style>
                {`
                    .glass-card { 
                        backdrop-filter: blur(12px); 
                        background: rgba(255, 255, 255, 0.7); 
                        border: 1px solid rgba(255, 255, 255, 0.5);
                        box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.05);
                    }
                    .glass-button {
                        backdrop-filter: blur(4px);
                        background: rgba(255, 255, 255, 0.1);
                        border: 1px solid rgba(255, 255, 255, 0.2);
                    }
                    .text-gradient {
                        background: linear-gradient(135deg, #1f2937 0%, #4b5563 100%);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                    }
                    .text-gradient-primary {
                         background: linear-gradient(to right, #EE3788, #523880);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                    }
                `}
            </style>

            {/* Hero */}
            <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24">
                {/* Abstract Background Blobs */}
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
                <div className="absolute top-0 right-1/4 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>

                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-2 text-xs font-bold text-secondary-600 mb-8 animate-fade-in-up">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                        Trusted by <span className="font-extrabold">300K+</span> creators
                    </div>

                    <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight mb-6 animate-fade-in-up delay-100">
                        Boost your <span className="text-gradient-primary">Growth</span>
                    </h1>

                    <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-200">
                        Unlock actionable insights with analytics to grow smarter, engage better, and monetize faster — all in one powered dashboard.
                    </p>

                    <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row animate-fade-in-up delay-300">
                        <a href="https://app.oyecreators.com/login" target="_blank" rel="noopener noreferrer" className="rounded-full bg-primary-500 px-8 py-4 text-base font-bold text-white hover:bg-primary-600 hover:shadow-lg hover:scale-105 transition-all duration-300 shadow-primary-500/30">
                            Try for free
                        </a>
                        {/* <Link to="#features" className="rounded-full glass-card px-8 py-4 text-base font-bold text-gray-700 hover:bg-white hover:shadow-md transition-all duration-300">
                            See how it works
                        </Link> */}
                    </div>
                </div>
            </section>

            {/* Feature Blocks */}
            <section id="features" className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
                <div className="grid gap-8 lg:gap-12">
                    {[
                        {
                            category: "Detailed account audit",
                            title: "Know your true stats",
                            desc: "Get a complete Instagram/TikTok audit: strengths, growth areas, engagement insights, audience quality and collaboration value.",
                            cta: "Get account audit",
                            visualColor: "bg-blue-50"
                        },
                        {
                            category: "Personalized growth insights",
                            title: "Boost your growth with data",
                            desc: "Remove the guesswork with clear guidance on what to post, when to post, and what to repeat — supported by daily tracking.",
                            cta: "Get growth strategy",
                            visualColor: "bg-pink-50"
                        },
                        {
                            category: "In-depth content analytics",
                            title: "Unlock higher engagement",
                            desc: "Understand what drives watch time, saves, shares, and comments — so you can refine your content strategy.",
                            cta: "Get insights to boost ER",
                            visualColor: "bg-purple-50"
                        },
                        {
                            category: "Viral content trends",
                            title: "Make your posts go viral",
                            desc: "Stay ahead with a frequently updated library of viral formats and hooks, so your content stays relevant.",
                            cta: "Get viral insights",
                            visualColor: "bg-orange-50"
                        }
                    ].map((feature, i) => (
                        <div key={i} className="group grid items-center gap-8 rounded-[2rem] glass-card p-8 lg:grid-cols-2 lg:p-12 hover:shadow-xl transition-all duration-500">
                            <div className={`${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                                <div className="text-xs font-bold uppercase tracking-wider text-secondary-500 mb-2">{feature.category}</div>
                                <h3 className="text-3xl font-extrabold tracking-tight text-gray-900 mb-4">{feature.title}</h3>
                                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                    {feature.desc}
                                </p>
                                {/* <Link to="/signup" className="inline-flex items-center gap-2 text-primary-600 font-bold hover:text-primary-700 transition-colors group-hover:translate-x-1 duration-300">
                                    {feature.cta} <span>→</span>
                                </Link> */}
                            </div>
                            <div className={`${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                                <div className={`aspect-[4/3] w-full rounded-3xl ${feature.visualColor} flex items-center justify-center border border-white/50 shadow-inner group-hover:scale-[1.02] transition-transform duration-500`}>
                                    {/* Placeholder for feature visual */}
                                    <div className="w-16 h-16 rounded-2xl bg-white/50 animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Testimonials (Enhanced) */}
            <section className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                {/* Decorative background elements */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-full bg-gradient-to-r from-transparent via-white/40 to-transparent blur-3xl -z-10"></div>

                <div className="flex flex-col md:flex-row items-end justify-between gap-6 mb-12">
                    <div>
                        <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-gray-900 mb-3">Trusted by creators</h2>
                        <p className="text-lg text-gray-600">Real outcomes from data-driven workflows.</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={prevSlide}
                            className="w-12 h-12 rounded-full glass-card flex items-center justify-center text-gray-700 hover:scale-110 hover:bg-white transition-all disabled:opacity-50 disabled:hover:scale-100"
                            disabled={carouselIndex === 0}
                        >
                            ←
                        </button>
                        <button
                            onClick={nextSlide}
                            className="w-12 h-12 rounded-full glass-card flex items-center justify-center text-gray-700 hover:scale-110 hover:bg-white transition-all disabled:opacity-50 disabled:hover:scale-100"
                            disabled={carouselIndex === testimonials.length - 1}
                        >
                            →
                        </button>
                    </div>
                </div>

                <div className="relative overflow-hidden rounded-[2.5rem] glass-card p-2">
                    <div
                        className="flex transition-transform duration-700 cubic-bezier(0.4, 0, 0.2, 1)"
                        style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
                    >
                        {testimonials.map((t, i) => (
                            <div key={i} className="w-full shrink-0 p-8 sm:p-14 lg:p-16 flex flex-col justify-center items-center text-center">
                                <div className="text-6xl text-primary-200 mb-6 font-serif">“</div>
                                <p className="text-2xl lg:text-3xl font-medium leading-relaxed text-gray-800 max-w-4xl mx-auto">
                                    {t.text}
                                </p>
                                <div className="mt-10 flex items-center gap-4">
                                    <div className={`h-12 w-12 rounded-full ${t.avatarColor} flex items-center justify-center font-bold text-lg`}>
                                        {t.handle[0].toUpperCase()}
                                    </div>
                                    <div className="text-left">
                                        <div className="text-base font-bold text-gray-900">{t.handle}</div>
                                        <div className="text-sm text-secondary-500 font-medium">{t.followers}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Closing CTA (Enhanced) */}
            <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
                <div className="relative rounded-[3rem] overflow-hidden p-12 lg:p-20 text-center text-white shadow-2xl shadow-indigo-900/20 group">
                    {/* Brand Gradient Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary-900 via-secondary-700 to-primary-800 transition-transform duration-1000 group-hover:scale-105"></div>

                    {/* Animated shapes */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-blob"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

                    <div className="relative z-10 max-w-3xl mx-auto">
                        <h3 className="text-4xl lg:text-5xl font-extrabold tracking-tight mb-6">Join them now</h3>
                        <p className="text-xl text-white/90 mb-10 leading-relaxed">
                            Start growing smarter, easier, and faster today — with analytics built for creators and teams.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                           <a href="https://app.oyecreators.com/login" target="_blank" rel="noopener noreferrer" className="rounded-full bg-white px-8 py-4 text-base font-bold text-secondary-900 hover:bg-gray-50 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                                Try for free
                            </a>
                            <Link to="/contact" className="rounded-full border border-white/30 bg-white/10 backdrop-blur-sm px-8 py-4 text-base font-bold text-white hover:bg-white/20 hover:-translate-y-1 transition-all duration-300">
                                Contact sales
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CreatorsLandingPage;
